import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { DateTime } from 'luxon';
import { RootState } from '../../../store';
import { formatTimeOffset } from '../../shared/date-time.formater';
import { CompanyFormat, datePattern, formatDate, formatMoney } from '../../shared/company-format';
import apiClient from '../../shared/auth.interceptor';
import GenericTable, { TableColumn } from '../../shared/tables/generic.table';

type Choice = { _id: string; name: string };
type Event = {
  _id: string;
  createdAt: string;
  companyName: string;
  actorName: string;
  templateName?: string;
  timezone?: string | null;
  // The company's QuickBooks date and currency formats (null for a removed company).
  dateFormat?: string | null;
  numberFormat?: string | null;
  currency?: string | null;
  type: string;
  details: Record<string, any>;
};
type Result = { items: Event[]; total: number; pageSize: number; actors: Choice[]; clients: Choice[]; types: string[] };
const labels: Record<string, string> = {
  'template.created': 'Template created',
  'template.updated': 'Template updated',
  'template.deleted': 'Template deleted',
  'qbo.connected': 'QuickBooks connected',
  'qbo.reconnected': 'QuickBooks reconnected',
  'qbo.disconnected': 'QuickBooks disconnected',
  'posting.success': 'Posting succeeded',
  'posting.error': 'Posting failed'
};
const fieldName = (field: string) =>
  ({
    _id: 'Template ID',
    user_id: 'Created by (user ID)',
    realmId: 'Company ID',
    calcNumber: 'Calculation day',
    postingNumber: 'Posting day offset',
    txn: 'Document number',
    ratePerJournalEntry: 'Percentage',
    balanceType: 'Accounting basis'
  })[field] || field.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
// Dates and balances follow the company's QuickBooks formats.
type Display = { format: CompanyFormat; zone: string };
const MONEY_FIELDS = new Set(['referenceBalance', 'referenceAccountBalance', 'amount', 'debitAmount', 'creditAmount']);
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const eventFormat = (event: Event): CompanyFormat => ({
  date: event.dateFormat,
  number: event.numberFormat,
  currency: event.currency
});
const valueText = (value: any, display: Display, field = ''): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number' && MONEY_FIELDS.has(field)) return formatMoney(value, display.format);
  if (typeof value === 'string' && ISO_DATE.test(value)) return formatDate(value, display.format);
  if (typeof value === 'string' && ISO_TIMESTAMP.test(value)) {
    const when = DateTime.fromISO(value, { zone: display.zone });
    if (when.isValid) return `${when.toFormat(datePattern(display.format))} ${when.toFormat('HH:mm:ss')}`;
  }
  if (typeof value !== 'object') return String(value);
  if (value.Name) return `${value.Name}${value.Id ? ` (${value.Id})` : ''}`;
  if (value.hour !== undefined)
    return `${String(value.hour).padStart(2, '0')}:${String(value.minute).padStart(2, '0')}`;
  if (Array.isArray(value)) return value.map((v) => valueText(v, display, field)).join('\n');
  return Object.entries(value)
    .map(([key, v]) => `${fieldName(key)}: ${valueText(v, display, key)}`)
    .join('\n');
};
const cellStyle = { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } as const;
// Times show the company's own time zone, the same way its journal templates do
// (e.g. "11/01/2026, 11:20:02 CST (UTC-6)"), or all events in UTC. A company with no
// time zone (or one since removed) shows UTC.
export const formatWhen = (
  iso: string,
  timezone: string | null | undefined,
  utc: boolean,
  format?: CompanyFormat
) => {
  const zone = utc || !timezone ? 'UTC' : timezone;
  const when = DateTime.fromISO(iso, { zone });
  return {
    date: when.toFormat(datePattern(format)),
    time: `${when.toFormat('HH:mm:ss')} ${zone === 'UTC' ? 'UTC' : formatTimeOffset(zone, false, when)}`
  };
};

function Details({ event, utc }: { event: Event; utc: boolean }) {
  const changes = event.details.changes as { field: string; before: any; after: any }[] | undefined;
  const display: Display = { format: eventFormat(event), zone: utc || !event.timezone ? 'UTC' : event.timezone };
  return (
    <Box sx={{ p: 2, bgcolor: '#f7f9fc' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {event.templateName || labels[event.type]}
      </Typography>
      <Table size="small" aria-label="Event details" sx={{ tableLayout: 'fixed' }}>
        {changes ? (
          <>
            <TableHead>
              <TableRow>
                <TableCell>Field changed</TableCell>
                <TableCell>Original entry</TableCell>
                <TableCell>Edited entry</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {changes.map((change) => (
                <TableRow key={change.field}>
                  <TableCell>{fieldName(change.field)}</TableCell>
                  <TableCell sx={cellStyle}>{valueText(change.before, display, change.field)}</TableCell>
                  <TableCell sx={cellStyle}>{valueText(change.after, display, change.field)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </>
        ) : (
          <TableBody>
            {Object.entries(event.details.snapshot || event.details).map(([field, value]) => (
              <TableRow key={field}>
                <TableCell sx={{ width: '30%' }}>{fieldName(field)}</TableCell>
                <TableCell sx={cellStyle}>{valueText(value, display, field)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        )}
      </Table>
    </Box>
  );
}
const columns: TableColumn[] = [
  { id: 'date', label: 'Date / time', minWidth: 190, wrap: false },
  { id: 'client', label: 'Client', minWidth: 210, maxWidth: 300, wrap: true },
  { id: 'actor', label: 'User', minWidth: 170, wrap: false },
  { id: 'type', label: 'Log type', minWidth: 165, wrap: false },
  { id: 'template', label: 'Template', minWidth: 140, wrap: false },
  { id: 'actions', label: 'Action', minWidth: 112, width: 112, wrap: false }
];
function FirmAuditLog({ firmId }: { firmId: string }) {
  const [filters, setFilters] = useState({ realmId: '', actorId: '', type: '', page: 1 });
  const [result, setResult] = useState<Result | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [utc, setUtc] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setResult(null);
    setExpandedId(null);
    apiClient
      .get<Result>('/v1/audit', { params: { firmId, ...filters } })
      .then(({ data }) => {
        if (!cancelled) setResult(data);
      })
      .catch(() => {
        if (!cancelled) setError('Unable to load the audit log. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [firmId, filters, revision]);
  // Keep filter choices visible while the next page is loading.
  const [choices, setChoices] = useState<Pick<Result, 'clients' | 'actors' | 'types'>>({
    clients: [],
    actors: [],
    types: []
  });
  useEffect(() => {
    if (result) setChoices(result);
  }, [result]);
  const select = (key: 'realmId' | 'actorId' | 'type', label: string, options: Choice[]) => (
    <TextField
      select
      label={label}
      size="small"
      value={filters[key]}
      sx={{ minWidth: 200 }}
      InputLabelProps={{ shrink: true }}
      SelectProps={{ displayEmpty: true }}
      onChange={(e) => setFilters((prev) => ({ ...prev, [key]: e.target.value, page: 1 }))}
    >
      <MenuItem value="">All</MenuItem>
      {options.map((option) => (
        <MenuItem key={option._id} value={option._id}>
          {option.name}
        </MenuItem>
      ))}
    </TextField>
  );
  return (
    <>
      <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 3, flexShrink: 0 }}>
        {select('realmId', 'Client', choices.clients)}
        {select('actorId', 'User', choices.actors)}
        {select(
          'type',
          'Log type',
          choices.types.map((type) => ({ _id: type, name: labels[type] || type }))
        )}
        <Button onClick={() => setRevision((value) => value + 1)} disabled={loading}>
          Refresh
        </Button>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={utc ? 'utc' : 'company'}
          onChange={(_, value) => value && setUtc(value === 'utc')}
          aria-label="Time zone"
          sx={{ ml: 'auto', alignSelf: 'center' }}
        >
          <ToggleButton value="company">Company time</ToggleButton>
          <ToggleButton value="utc">UTC</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {loading ? (
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <CircularProgress aria-label="Loading audit log" />
        </Box>
      ) : (
        result && (
          <>
            <GenericTable
              columns={columns}
              pagination={{
                page: filters.page,
                pageSize: result.pageSize,
                total: result.total,
                onPageChange: (page) => setFilters((prev) => ({ ...prev, page }))
              }}
              ariaLabel="Audit log"
              containerSx={{ mt: 0, flex: '1 1 0', minHeight: 0, minWidth: 0, mr: 0, pr: 0 }}
              rowKey={(row) => row.event._id}
              emptyMessage="No events match these filters."
              data={result.items.map((event) => ({
                event,
                date: (
                  <Box component="span">
                    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                      {formatWhen(event.createdAt, event.timezone, utc, eventFormat(event)).date},
                    </Box>{' '}
                    <Box component="span" sx={{ display: { xs: 'block', lg: 'inline' }, whiteSpace: 'nowrap' }}>
                      {formatWhen(event.createdAt, event.timezone, utc, eventFormat(event)).time}
                    </Box>
                  </Box>
                ),
                client: event.companyName,
                actor: event.actorName,
                type: (
                  <Chip
                    size="small"
                    label={labels[event.type] || event.type}
                    color={event.type === 'posting.error' ? 'error' : 'default'}
                    sx={{ height: 20, fontSize: '0.75rem' }}
                  />
                ),
                template: event.templateName || '—',
                actions: (
                  <Button
                    size="small"
                    sx={{
                      whiteSpace: 'nowrap',
                      wordBreak: 'normal',
                      minWidth: 80,
                      flexShrink: 0,
                      justifyContent: 'flex-start',
                      px: 0,
                      py: 0,
                      height: 'auto',
                      fontSize: '0.875rem'
                    }}
                    aria-expanded={expandedId === event._id}
                    onClick={() => setExpandedId(expandedId === event._id ? null : event._id)}
                  >
                    {expandedId === event._id ? 'Hide' : 'Details'}
                  </Button>
                )
              }))}
              renderDetails={(row) => (expandedId === row.event._id ? <Details event={row.event} utc={utc} /> : null)}
            />
          </>
        )
      )}
    </>
  );
}
export default function AuditLog() {
  const firm = useSelector((state: RootState) => state.currentFirm.currentFirm);
  return (
    <Box
      sx={{ height: '100%', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <Typography sx={{ mb: 3, fontSize: '1.5rem', fontWeight: 'bold', flexShrink: 0 }}>Audit Log</Typography>
      {firm?._id ? (
        <FirmAuditLog key={firm._id} firmId={firm._id} />
      ) : (
        <Alert severity="info">Select a firm to view its audit log.</Alert>
      )}
    </Box>
  );
}
